import { Trophy, RotateCcw, Star } from 'lucide-react';
import Button from './ui/Button';
import HighScoresDisplay from './HighScoresDisplay';
import { GAME_MODE_CONFIG } from '../config/gameConfig';

/**
 * GameOverScreen Component
 * Displays final stats and high scores
 */
export default function GameOverScreen({
  playerName,
  score,
  level,
  streak,
  difficulty,
  gameMode,
  isNewHighScore,
  highScores,
  onRestart,
}) {
  const modeConfig = GAME_MODE_CONFIG[gameMode];
  const maxStreak = streak; // In real implementation, track max streak separately

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 p-4 flex items-center justify-center">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">
            {isNewHighScore ? '🎉' : '👏'}
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            {isNewHighScore ? 'Ny rekord!' : 'Godt spillet!'}
          </h1>
          <p className="text-xl text-gray-600">
            Flot klaret, {playerName}!
          </p>
          {modeConfig && (
            <div className="mt-3 inline-flex items-center gap-2 bg-purple-100 px-4 py-2 rounded-full">
              <span className="text-2xl">{modeConfig.icon}</span>
              <span className="font-semibold text-purple-700">{modeConfig.label}</span>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-gradient-to-br from-purple-100 to-purple-200 p-6 rounded-2xl text-center">
            <Trophy className="mx-auto mb-2 text-purple-600" size={32} />
            <div className="text-3xl font-bold text-purple-700">{score.toLocaleString()}</div>
            <div className="text-sm text-purple-600 font-medium">Samlet Score</div>
          </div>

          <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-6 rounded-2xl text-center">
            <Star className="mx-auto mb-2 text-blue-600" size={32} />
            <div className="text-3xl font-bold text-blue-700">{level}</div>
            <div className="text-sm text-blue-600 font-medium">Niveau Nået</div>
          </div>

          <div className="bg-gradient-to-br from-orange-100 to-orange-200 p-6 rounded-2xl text-center">
            <div className="text-3xl mb-2">🔥</div>
            <div className="text-3xl font-bold text-orange-700">{maxStreak}</div>
            <div className="text-sm text-orange-600 font-medium">Bedste Række</div>
          </div>

          <div className="bg-gradient-to-br from-green-100 to-green-200 p-6 rounded-2xl text-center">
            <div className="text-3xl mb-2">✅</div>
            <div className="text-3xl font-bold text-green-700">
              {Math.floor((score / (level * 10)) || 0)}
            </div>
            <div className="text-sm text-green-600 font-medium">Rigtige Svar</div>
          </div>
        </div>

        {/* High Scores */}
        <div className="mb-8">
          <HighScoresDisplay
            highScores={highScores}
            currentScore={isNewHighScore ? { playerName, score } : null}
          />
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <Button
            onClick={onRestart}
            size="lg"
            fullWidth
            className="text-2xl"
          >
            <RotateCcw className="inline mr-2" size={24} />
            Spil Igen
          </Button>
        </div>
      </div>
    </div>
  );
}
